// Account HTTP API, client side (protocol §3.2; shapes in @ballio/shared account.ts):
// - `fetchAccountMe`  GET  /api/account/me                → the signed-in account (created on first
//                     sight), or an auth error.
// - `setUsername`     POST /api/account/username {username} → ok | invalid | taken | alreadySet.
// - `linkWallet`      POST /api/account/wallet {identityToken} → ok | noWallet | walletMismatch |
//                     identityStale.
// Every call sends `Authorization: Bearer <access token>` (auth/auth.ts `getAccessToken`). Never
// rejects: transport failures come back as `{ status: 'error', error: 'network' }`.
//
// Contracts step: complete (the server routes land with lane A). Consumers: auth/*.ts (account
// refresh after sign-in), the username prompt (client builder).

import type { ApiAccountMe, ApiAuthError, UsernameProblem } from '@ballio/shared';
import { NET_TIMING } from '@ballio/shared';
import { serverUrl } from './server-url.js';

export interface AccountHttpOptions {
  /** Server base (net/server-url.ts); `""` = this page. */
  readonly baseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  /** Abort after this many ms. Default 5000. */
  readonly timeoutMs?: number;
}

export type AccountError = ApiAuthError | 'network';

export type AccountResult =
  | { readonly status: 'ok'; readonly account: ApiAccountMe }
  | { readonly status: 'error'; readonly error: AccountError; readonly httpStatus: number };

export type SetUsernameResult =
  | AccountResult
  | {
      readonly status: 'error';
      readonly error: 'invalid';
      readonly problem: UsernameProblem;
      readonly httpStatus: number;
    }
  | {
      readonly status: 'error';
      readonly error: 'taken' | 'alreadySet';
      readonly httpStatus: number;
    };

export type LinkWalletResult =
  | AccountResult
  | {
      readonly status: 'error';
      readonly error: 'noWallet' | 'walletMismatch' | 'identityStale';
      readonly httpStatus: number;
    };

const AUTH_ERRORS: readonly ApiAuthError[] = [
  'authRequired',
  'authInvalid',
  'authUnavailable',
  'banned',
];
const USERNAME_PROBLEMS: readonly UsernameProblem[] = [
  'tooShort',
  'tooLong',
  'badChars',
  'reserved',
];

function asRecord(v: unknown): Record<string, unknown> | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

/** Validates an ApiAccountMe body; null when malformed. */
export function parseAccountMe(raw: unknown): ApiAccountMe | null {
  const b = asRecord(raw);
  if (!b || b['status'] !== 'ok' || typeof b['accountId'] !== 'string' || b['accountId'] === '') {
    return null;
  }
  const str = (v: unknown): string | null => (typeof v === 'string' && v !== '' ? v : null);
  const ms = (v: unknown): number | null =>
    typeof v === 'number' && Number.isFinite(v) ? v : null;
  return {
    status: 'ok',
    accountId: b['accountId'],
    username: str(b['username']),
    wallet: str(b['wallet']),
    walletUnlinkedAt: str(b['wallet']) === null ? null : ms(b['walletUnlinkedAt']),
    createdAt: ms(b['createdAt']) ?? 0,
    bannedUntil: ms(b['bannedUntil']),
  };
}

async function call(
  method: 'GET' | 'POST',
  path: string,
  token: string,
  body: unknown,
  opts: AccountHttpOptions | undefined,
): Promise<{ status: number; body: Record<string, unknown> | null } | null> {
  const doFetch: typeof fetch = opts?.fetchImpl ?? ((input, init) => globalThis.fetch(input, init));
  const ctrl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = setTimeout(
    () => ctrl?.abort(),
    opts?.timeoutMs ?? NET_TIMING.connectAttemptTimeoutSec * 1000,
  );
  try {
    const headers: Record<string, string> = { authorization: `Bearer ${token}` };
    if (body !== undefined) headers['content-type'] = 'application/json';
    const res = await doFetch(serverUrl(opts?.baseUrl ?? '', path), {
      method,
      headers,
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
      signal: ctrl?.signal ?? null,
    });
    let parsed: unknown = undefined;
    try {
      parsed = await res.json();
    } catch {
      parsed = undefined;
    }
    return { status: res.status, body: asRecord(parsed) };
  } catch {
    return null;
  } finally {
    clearTimeout(timer);
  }
}

function accountOrAuthError(
  r: { status: number; body: Record<string, unknown> | null } | null,
): AccountResult {
  if (r === null) return { status: 'error', error: 'network', httpStatus: 0 };
  const me = parseAccountMe(r.body);
  if (r.status === 200 && me !== null) return { status: 'ok', account: me };
  const code = r.body?.['error'];
  const error = (AUTH_ERRORS as readonly unknown[]).includes(code)
    ? (code as ApiAuthError)
    : 'network';
  return { status: 'error', error, httpStatus: r.status };
}

export async function fetchAccountMe(
  token: string,
  opts?: AccountHttpOptions,
): Promise<AccountResult> {
  return accountOrAuthError(await call('GET', '/api/account/me', token, undefined, opts));
}

export async function setUsername(
  token: string,
  username: string,
  opts?: AccountHttpOptions,
): Promise<SetUsernameResult> {
  const r = await call('POST', '/api/account/username', token, { username }, opts);
  const code = r?.body?.['error'];
  if (r !== null && code === 'invalid') {
    const p = r.body?.['problem'];
    const problem = (USERNAME_PROBLEMS as readonly unknown[]).includes(p)
      ? (p as UsernameProblem)
      : 'badChars';
    return { status: 'error', error: 'invalid', problem, httpStatus: r.status };
  }
  if (r !== null && (code === 'taken' || code === 'alreadySet')) {
    return { status: 'error', error: code, httpStatus: r.status };
  }
  return accountOrAuthError(r);
}

export async function linkWallet(
  token: string,
  identityToken: string,
  opts?: AccountHttpOptions,
): Promise<LinkWalletResult> {
  const r = await call('POST', '/api/account/wallet', token, { identityToken }, opts);
  const code = r?.body?.['error'];
  if (
    r !== null &&
    (code === 'noWallet' || code === 'walletMismatch' || code === 'identityStale')
  ) {
    return { status: 'error', error: code, httpStatus: r.status };
  }
  return accountOrAuthError(r);
}
